import Skeleton from "@mui/material/Skeleton";
import { RefreshCw, TriangleAlert } from "lucide-react";
import * as React from "react";

import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { RobotoButton } from "@/shared/foundation/primitives";

import { type ActivityLoader, useActivityResult } from "./ActivityLoader";
import { NEVER } from "./loading";
import styles from "./Timeline.module.css";
import {
  type TimelineDataStreamer,
  useTimelineData,
} from "./TimelineDataStreamer";
import { TimelineMessage } from "./TimelineMessage";
import type { TimeWindow } from "./window";

interface TimelineBoundaryProps {
  /** What to show once the subtree fails; `retry` starts the request over and remounts the subtree. */
  renderError: (retry: () => void, error: Error) => React.ReactNode;
  /** Shown while the subtree suspends. */
  fallback: React.ReactNode;
  /** Start the request over; the boundary forgets its error afterwards. */
  onRetry: () => void;
  /** The source behind the subtree (a loader or streamer); a new one forgets a stale error too. */
  source: unknown;
}

/**
 * An error boundary with a retry around a Suspense boundary.
 */
export function TimelineBoundary({
  renderError,
  fallback,
  onRetry,
  source,
  children,
}: React.PropsWithChildren<TimelineBoundaryProps>) {
  const [attempt, setAttempt] = React.useState(0);
  // A new source counts as an attempt. Compared during render (React's
  // derived-state pattern) so no effect or ref is needed.
  const [seenSource, setSeenSource] = React.useState(source);
  if (source !== seenSource) {
    setSeenSource(source);
    setAttempt((n) => n + 1);
  }
  return (
    <ErrorBoundary
      key={attempt}
      fallback={({ error }) =>
        renderError(() => {
          onRetry();
          setAttempt((n) => n + 1);
        }, error)
      }
    >
      <React.Suspense fallback={fallback}>{children}</React.Suspense>
    </ErrorBoundary>
  );
}

/**
 * Suspends until the activity — the history's extent and buckets — has
 * loaded, and throws its error.
 */
export function ActivityGate({
  loader,
  children,
}: React.PropsWithChildren<{ loader: ActivityLoader | null }>) {
  const result = useActivityResult(loader);
  if (loader === null || result.status === "pending") {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- a thrown promise is how a component suspends (React 18); `use()` replaces it in React 19
    throw loader?.whenLoaded() ?? NEVER;
  }
  if (result.status === "error") {
    throw result.error;
  }
  return children;
}

/**
 * Asks the streamer for the window and suspends until it can answer.
 */
export function DataGate<T>({
  streamer,
  window,
  children,
}: React.PropsWithChildren<{
  streamer: TimelineDataStreamer<T>;
  window: TimeWindow;
}>) {
  const result = useTimelineData(streamer, window);
  if (result.status === "pending") {
    streamer.requestData(window);
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- a thrown promise is how a component suspends (React 18); `use()` replaces it in React 19
    throw streamer.whenLoaded();
  }
  if (result.status === "error") {
    throw result.error;
  }
  return children;
}

/** Error UI with retry when the timeline activity fails to load. */
export function TimelineFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <TimelineMessage
      tone="danger"
      icon={<TriangleAlert aria-hidden="true" />}
      title="Couldn't load the timeline"
      action={
        <RobotoButton
          variant="secondary"
          icon={<RefreshCw aria-hidden="true" />}
          onClick={onRetry}
        >
          Retry
        </RobotoButton>
      }
    />
  );
}

/** The track area when its chunk failed to load; the strip and footer stay. */
export function TracksFailed({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) {
  return (
    <div className={styles.failed} role="alert">
      <p className={styles.failedMessage}>
        Couldn&apos;t load the sessions in this span: {error.message}
      </p>
      <RobotoButton variant="secondary" size="sm" onClick={onRetry}>
        Retry
      </RobotoButton>
    </div>
  );
}

/** The whole timeline before its history is known. */
export function TimelineSkeleton() {
  return (
    <div className={styles.timeline} aria-busy="true">
      {/* Stand-ins for the previous / next buttons and the date range. */}
      <div className={styles.controlsSkeleton}>
        <Skeleton variant="text" width={56} />
        <Skeleton variant="text" width={240} />
      </div>
      <div className={styles.activitySkeleton} />
      <TracksSkeleton />
      <div className={styles.footerSkeleton} />
    </div>
  );
}

/** The track area before its chunk is loaded; the axis and footer stay. */
export function TracksSkeleton() {
  return (
    <div className={styles.tracksSkeleton} role="status">
      <span className="sr-only">Loading sessions</span>
    </div>
  );
}
